import { useEffect, useRef, useState } from 'react';
import s from './Process.module.css';
import { I } from '@narrative-studio/ui';
import { Terrain, surfacePoint, type Relief } from './Terrain.tsx';

/* The process, as a scroll-driven split: numbered steps on the left, and on
   the right a sticky isometric stack of six layer slabs — one per MLAP layer,
   textured with a dot grid — that rearranges itself for whichever step is in
   view. Drawn as SVG so the "pixel" texture stays crisp and every change is a
   CSS transition, not an image swap. */

export type Mode = 'sources' | 'passes' | 'unit' | 'interrogate' | 'releases';

const STEPS: { n: string; key: Mode; title: string; body: string; tag: string }[] = [
  { n: '01', key: 'sources', tag: 'Bring', title: 'Bring the material.', body: 'A policy paper, a debate transcript, a page of field notes, a web page, a chat export. Parsed in your browser, stored once, kept in your project.' },
  { n: '02', key: 'passes', tag: 'Read', title: 'Read in seven gated passes.', body: 'Each pass produces one layer against a pinned Knowledge Release. Between passes, deterministic gates check the work: every finding cites, confidence is ordinal, loops close or are recorded open.' },
  { n: '03', key: 'unit', tag: 'Map', title: 'Get a Narrative Unit.', body: 'Six layers, one map: Meaning, Landscape, Architecture, Pathways, Emerging issues, Transformation opportunities — and the dark spots, because silence is data.' },
  { n: '04', key: 'interrogate', tag: 'Interrogate', title: 'Ask it. Correct it.', body: 'Which frame dominates? Who is never positioned? Answers cite the unit\'s own sentences. Re-type a pathway or open a loop; your corrections travel with the unit.' },
  { n: '05', key: 'releases', tag: 'Compare', title: 'Compare over time.', body: 'Releases are pinned per project, so a unit made in March still means what it meant in March. Re-run on a newer release and see exactly what the instrument changed.' },
];

/* Each step sits in a tall slot and is sticky within it, the same height as
   the visual: it docks below the nav, holds while its slot scrolls, then
   leaves as the next one arrives. The right column redraws for whichever
   step has crossed the middle of the screen. */
const TOP = 72;     // below the sticky nav

/* Below 860px there is no room for a second column: each step carries its
   own visual, directly under its text, so the copy and the picture stay together. */
function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && matchMedia('(max-width: 860px)').matches);
  useEffect(() => {
    const mq = matchMedia('(max-width: 860px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}
const Visual = ({ mode }: { mode: Mode }) => mode === 'sources' ? <Ingest /> : <Stack mode={mode} />;

export function Process() {
  const narrow = useNarrow();
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    if (narrow) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      let a = 0;
      const line = TOP + (window.innerHeight - TOP) / 2;
      refs.current.forEach((el, i) => { if (el && el.getBoundingClientRect().top <= line) a = i; });
      setActive(a);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [narrow]);
  if (narrow) {
    return (
      <section className={s.wrap} id="how">
        <div className={s.left}>
          {STEPS.map(st => (
            <div key={st.key} className={`${s.step} ${s.active}`}>
              <div className={s.eyebrow}><span className={s.n}>{st.n}</span><span className={s.tag}>{st.tag}</span></div>
              <h2 className={s.title}>{st.title}</h2>
              <p className={s.body}>{st.body}</p>
              <div className={s.inline}><Visual mode={st.key} /></div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className={s.wrap} id="how">
      <div className={s.left}>
        {STEPS.map((st, i) => (
          <div key={st.key} className={s.slot}>
            <div ref={el => { refs.current[i] = el; }} className={[s.step, i === active && s.active].filter(Boolean).join(' ')}>
              <div className={s.eyebrow}><span className={s.n}>{st.n}</span><span className={s.tag}>{st.tag}</span></div>
              <h2 className={s.title}>{st.title}</h2>
              <p className={s.body}>{st.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div className={s.right}>
        <div className={s.sticky}><Visual mode={STEPS[active]!.key} /></div>
      </div>
    </section>
  );
}

/* Step 01: the product itself — Studio's "Add sources" sheet, drawn in
   markup so it stays crisp and its rows can arrive one by one. */
const SOURCES: { icon: keyof typeof I; name: string; meta: string; state: 'ready' | 'parsing' }[] = [
  { icon: 'doc', name: 'Workforce Plan 2026 — executive summary.pdf', meta: '12 pages · parsed in your browser', state: 'ready' },
  { icon: 'folder', name: 'Field notes / ward visits', meta: '7 files · synced by content hash', state: 'ready' },
  { icon: 'chat', name: 'ChatGPT export — retention thread.json', meta: '41 turns · assistant turns kept as context', state: 'ready' },
  { icon: 'link', name: 'hansard.parliament.uk/…/nursing-recruitment', meta: 'fetched · 2,140 words', state: 'parsing' },
];
function Ingest() {
  return (
    <div className={s.mock}>
      <div className={s.mockBar}><I.plus /><span>Add sources</span><span className={s.mockCrumb}>Health workforce</span></div>
      <div className={s.mockTabs}>
        {([['files', 'Files'], ['folder', 'Folder'], ['chat', 'Chat export'], ['link', 'URL']] as [keyof typeof I, string][]).map(([ic, l], i) => { const Icon = I[ic]; return <span key={l} className={[s.mockTab, i === 0 && s.mockTabOn].filter(Boolean).join(' ')}><Icon />{l}</span>; })}
      </div>
      <div className={s.mockDrop}><I.inbox /><div><b>Drop files here</b>, or choose them.</div><div className={s.mockFaint}>PDF, Word, Markdown, plain text. Parsed here, in the browser.</div></div>
      <div className={s.mockRows}>
        {SOURCES.map((r, i) => { const Icon = I[r.icon]; return (
          <div key={r.name} className={s.mockRow} style={{ animationDelay: `${0.25 + i * 0.16}s` }}>
            <span className={s.mockIcon}><Icon /></span>
            <span className={s.mockName}>{r.name}<span className={s.mockFaint}>{r.meta}</span></span>
            <span className={[s.mockPill, r.state === 'parsing' && s.mockPillAmber].filter(Boolean).join(' ')}>{r.state === 'ready' ? 'ready' : 'parsing…'}</span>
          </div>); })}
      </div>
      <div className={s.mockFoot}><span className={s.mockFaint}>Stored once in your project. Nothing leaves it.</span><span className={s.mockBtn}>Read 4 sources <I.arrow /></span></div>
    </div>
  );
}

/* One relief per layer, monochrome: the stack is six different landscapes. */
const LAYERS: { key: string; label: string; relief: Relief }[] = [
  { key: 'meaning', label: 'Meaning', relief: 'rolling' },
  { key: 'landscape', label: 'Landscape', relief: 'ridge' },
  { key: 'architecture', label: 'Architecture', relief: 'terraces' },
  { key: 'pathways', label: 'Pathways', relief: 'channel' },
  { key: 'emerging', label: 'Emerging issues', relief: 'peaks' },
  { key: 'opportunities', label: 'Opportunities', relief: 'crater' },
];

/* The pixel-wave grid on the top slab: 16 × 16 points in surface coordinates. */
const WAVE = Array.from({ length: 16 * 16 }, (_, k) => ({ u: (k % 16 + 0.5) / 16, v: (Math.floor(k / 16) + 0.5) / 16 }));

/* Isometric slab: top face is a rhombus, two side faces give it thickness. */
function slabPath(cx: number, cy: number, w: number, d: number, t: number) {
  const top = `M${cx},${cy - d / 2} L${cx + w / 2},${cy} L${cx},${cy + d / 2} L${cx - w / 2},${cy} Z`;
  const left = `M${cx - w / 2},${cy} L${cx},${cy + d / 2} L${cx},${cy + d / 2 + t} L${cx - w / 2},${cy + t} Z`;
  const right = `M${cx},${cy + d / 2} L${cx + w / 2},${cy} L${cx + w / 2},${cy + t} L${cx},${cy + d / 2 + t} Z`;
  return { top, left, right };
}

function Stack({ mode }: { mode: Mode }) {
  const W = 520, H = 470, cx = 250, w = 250, d = 125, t = 14, gap = 44, base = 340;
  const [passIndex, setPassIndex] = useState(0);
  useEffect(() => {
    if (mode !== 'passes') return;
    setPassIndex(0);
    const id = window.setInterval(() => setPassIndex(p => (p + 1) % 6), 650);
    return () => clearInterval(id);
  }, [mode]);
  const lit = (i: number) => mode === 'unit' || mode === 'releases' || (mode === 'passes' && i <= passIndex) || (mode === 'interrogate' && i === 3);
  const lift = (i: number) => mode === 'passes' ? (i === passIndex ? 22 : 0) : mode === 'unit' ? i * 5 : 0;
  const shift = (i: number) => mode === 'interrogate' && i === 3 ? 120 : 0;
  const ink = '#18181B', mute = '#9A9AA2', soft = '#52525B';

  return (
    <svg className={s.svg} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {/* Ghost of the previous release, for "compare" */}
      <g className={s.ghost} style={{ opacity: mode === 'releases' ? 1 : 0, transform: mode === 'releases' ? 'translate(-150px, -40px)' : 'translate(-60px, -40px)' }}>
        {LAYERS.map((l, i) => <Terrain key={l.key} relief={l.relief} cx={cx} cy={base - i * gap} w={w} d={d} t={t} lit={false} ghost amp={0.8} />)}
        <text x={cx} y={base + 96} fontSize="11.5" fontWeight="700" fill={mute} letterSpacing="1.2" textAnchor="middle">RELEASE 0.2.0</text>
      </g>

      {/* The stack */}
      {LAYERS.map((l, i) => {
        const y = base - i * gap; const on = lit(i);
        const shiftX = shift(i) + (mode === 'releases' ? 40 : 0);
        return (
          <g key={l.key} className={s.slab} style={{ transform: `translate(${shiftX}px, ${-lift(i)}px)`, opacity: mode === 'sources' && i < 5 ? 0.6 : 1 }}>
            <Terrain relief={l.relief} cx={cx} cy={y} w={w} d={d} t={t} lit={on} />
            {(mode === 'unit' || mode === 'releases') && <text x={cx + w / 2 + 14} y={y + 4} fontSize="11" fontWeight="700" fill={on ? ink : mute} letterSpacing="1" style={{ transition: 'fill .4s' }}>{l.label.toUpperCase()}</text>}
          </g>
        );
      })}

      {/* Sources: documents above the stack, their lines streaming down as
          pixels, and a pixel wave rolling across the top layer's surface */}
      <g className={s.sheets} style={{ opacity: mode === 'sources' ? 1 : 0, transform: mode === 'sources' ? 'translate(0,0)' : 'translate(0,-30px)' }}>
        {[0, 1, 2].map(i => {
          const ox = cx + (i - 1) * 26, y = base - 5 * gap - 96 - i * 36; const p = slabPath(ox, y, 150, 75, 4);
          return <g key={i}>
            <path d={p.left} fill="#D4D4D8" /><path d={p.right} fill="#C9C9CE" /><path d={p.top} fill="#FFFFFF" stroke="#D4D4D8" />
            {[0, 1, 2, 3].map(r => Array.from({ length: 9 - (r === 3 ? 4 : 0) }, (_, c) => {
              const px = ox - 44 + r * 10 + c * 8, py = y - 22 + r * 10 + c * 4;
              return <rect key={`${r}-${c}`} className={s.px} x={px} y={py} width="4" height="2.4" fill={r === 0 ? ink : soft} style={{ animationDelay: `${-(r * 0.35 + c * 0.09 + i * 0.5)}s` }} />;
            }))}
          </g>;
        })}
        {/* the stream: pixels falling from the sheets into the slab */}
        {Array.from({ length: 14 }, (_, k) => <rect key={k} className={s.drop} x={cx - 30 + (k % 7) * 10} y={base - 5 * gap - 60} width="3" height="3" fill={mute} style={{ animationDelay: `${-(k * 0.23)}s`, animationDuration: `${1.6 + (k % 3) * 0.3}s` }} />)}
        <text x={cx} y={base - 5 * gap - 214} fontSize="11" fontWeight="700" fill={soft} letterSpacing="1.2" textAnchor="middle">PDF · WORD · WEB · CHAT EXPORT</text>
      </g>
      <g style={{ opacity: mode === 'sources' ? 1 : 0, transition: 'opacity .45s' }}>
        {WAVE.map(({ u, v }) => { const q = surfacePoint(LAYERS[5]!.relief, cx, base - 5 * gap, w, d, u, v); return <rect key={`${u}-${v}`} className={s.wave} x={q.X - 1.5} y={q.Y - 3.5} width="3" height="3" fill={ink} style={{ animationDelay: `${-((u + v) * 1.6)}s` }} />; })}
      </g>

      {/* Passes: gate marks beside the stack */}
      <g style={{ opacity: mode === 'passes' ? 1 : 0, transition: 'opacity .4s' }}>
        {LAYERS.map((_, i) => <g key={i}><circle cx={cx - w / 2 - 34} cy={base - i * gap} r="5.5" fill={i < passIndex ? ink : 'none'} stroke={i <= passIndex ? ink : '#D4D4D8'} strokeWidth="1.6" style={{ transition: 'fill .3s, stroke .3s' }} /><text x={cx - w / 2 - 48} y={base - i * gap + 4} fontSize="11" fontWeight="600" fill={mute} textAnchor="end">P{i + 1}</text></g>)}
        <text x={cx - w / 2 - 34} y={base - 6 * gap - 6} fontSize="11" fontWeight="700" fill={soft} letterSpacing="1.2" textAnchor="middle">GATES</text>
      </g>

      {/* Interrogate: a quote pinned to the lifted-out pathways slab */}
      <g style={{ opacity: mode === 'interrogate' ? 1 : 0, transform: mode === 'interrogate' ? 'translate(0,0)' : 'translate(0,10px)', transition: 'opacity .45s .15s, transform .45s .15s' }}>
        <rect x={cx - 120} y={base + 44} width={330} height={62} rx="10" fill="#fff" stroke="#E2E2E5" />
        <text x={cx - 104} y={base + 66} fontSize="10.5" fontWeight="700" fill={mute} letterSpacing="1">PATHWAY · BLOCKED · CONFIDENCE MEDIUM</text>
        <text x={cx - 104} y={base + 88} fontSize="12" fill="#3A3833">“…the rostering system cannot express what they need.”</text>
      </g>

      {/* Releases: version tags */}
      <g style={{ opacity: mode === 'releases' ? 1 : 0, transition: 'opacity .4s .2s' }}>
        <text x={cx + 40} y={base + 96} fontSize="11.5" fontWeight="700" fill={ink} letterSpacing="1.2" textAnchor="middle">RELEASE 0.3.0</text>
        <text x={cx + 40} y={base + 114} fontSize="11" fill={soft} textAnchor="middle">2 of 11 pathways re-typed</text>
      </g>
    </svg>
  );
}
